// File for logic corresponding to header component
import { Component } from 'js-component-framework';
import breakpoints from '../../js/utils/breakpoints';

/**
 * Handles class appending to non-classic, opens, or elite tournament wems.
 *
 * @param {bool} isSticky whether or not the header is stuck.
 */
const handleWem = (isSticky) => {
  const tournaments = document.querySelectorAll('.bass-component__single-wem-tournament');

  if (!tournaments) {
    return;
  }

  tournaments.forEach((tournament) => {
    const focusClasses = [
      'bass-component__single-wem-tournament--elite',
      'bass-component__single-wem-tournament--opens',
      'bass-component__single-wem-tournament--classic',
    ];

    const hasClass = focusClasses.some((className) => tournament.classList.contains(className));

    if (!hasClass && isSticky) {
      tournament.classList.add('un-stuck');
    } else {
      tournament.classList.remove('un-stuck');
    }
  });
};

/**
 * Handles sticking the header via class. Stuck done in styles.
 *
 * @param {element} header header element.
 */
const stickHeader = (header) => {
  if (!header) {
    return;
  }

  if (window.scrollY > 0) {
    header.classList.add('sticky');
  } else {
    header.classList.remove('sticky');
  }

  // Handle removing wem items if applicable.
  handleWem(window.scrollY > 0);
};

/**
 * Handles the padding offset for the header and content.
 *
 * @param {element} offset offset element.
 * @param {element} header header element.
 * @param {element} adSlot leaderboard ad element.
 */
const handleSticky = (headerOffset, adOffset, header, adSlot) => {
  const isLargeAd = adSlot.querySelector('iframe')?.height > 90;
  const isMobile = !breakpoints.mdMin.matches;

  if (!headerOffset || !header || isLargeAd || isMobile) return;

  const isMobileMenuBreakpoint = breakpoints.mobileMenuMin.matches;

  const headerHeight = isMobileMenuBreakpoint ? 102 : 72;
  const headerHeightDifference = isMobileMenuBreakpoint ? 35 : 0;
  const adSlotHeight = isMobileMenuBreakpoint ? 171 : 139;

  const wem = document.querySelector('#bass-component__wem');

  // Leaves room for the WEM, if necessary.
  let headerWemOffset = 0;
  if (wem?.offsetHeight > 0) {
    headerWemOffset = wem.offsetHeight + headerHeightDifference;
  }

  // Only need an ad slot offset if it's present and has been stickied.
  // Factor in the window height minus ad-slot height for an elegant unstickying.
  let adSlotOffset = 0;
  if (adSlot
    && (window.scrollY > headerWemOffset)
      && window.scrollY < (1.5 * (window.innerHeight - adSlotHeight))) {
    adSlotOffset = adSlotHeight;
    adSlot.classList.add('sticky');
    adOffset.style.paddingTop = `${adSlotOffset}px`; // eslint-disable-line no-param-reassign

    if (wem) {
      wem.style.zIndex = 9;
    }
  } else {
    adSlot.classList.remove('sticky');
    adOffset.style.paddingTop = 0; // eslint-disable-line no-param-reassign

    if (wem) {
      wem.style.zIndex = 10;
    }
  }

  headerOffset.style.paddingTop = `${headerHeight || 72}px`; // eslint-disable-line no-param-reassign
  header.style.position = 'fixed'; // eslint-disable-line no-param-reassign
};

/**
 *  Set tab index for mobile/desktop.
 *
 * @param {bool} matches has breakpoint matches.
 */
const setTabindexes = (matches) => {
  if (matches) {
    document.querySelectorAll('[data-mobile-tabindex="-1"]').forEach((el) => {
      el.removeAttribute('tabIndex');
    });
  } else {
    document.querySelectorAll('[data-mobile-tabindex="-1"]').forEach((el) => {
      el.setAttribute('tabIndex', -1);
    });
  }
};

/**
 * Get siblings for a selector.
 *
 * @param {node} parent parent selector.
 * @param {node} selector current selector
 *
 * @returns sibilings for a parent.
 */
const siblings = (parent, selector = null) => {
  if (parent.parentNode === null) return [];

  const sibs = Array.prototype.filter.call(parent.parentNode.children, (child) => child !== parent);

  if (selector === null) return sibs;

  return sibs.filter((el) => el.matches(selector));
};

/**
 * Component for site header
 */
export default class Header extends Component {
  /**
   * Start the component
   */
  constructor(config) {
    super(config);

    // Other Options
    this.offset = this.options.offset;

    setTabindexes(breakpoints.lgMin.matches);
    breakpoints.lgMin.addEventListener('change', (e) => setTabindexes(e.matches));

    // Globally accessible elements.
    const header = document.getElementById('masthead');
    const adSlot = document.querySelector('.top-leaderboard');
    const headerOffset = document.querySelector('.bass-element__pushdown');
    const adOffset = document.querySelector('.bass-element__top-ad-pushdown');

    /**
     * Handle scrolling function of header.
     */
    window.onscroll = () => {
      stickHeader(header);
      handleSticky(headerOffset, adOffset, header, adSlot);
    };
    window.addEventListener('load', () => handleSticky(headerOffset, adOffset, header, adSlot));
    window.addEventListener('resize', () => handleSticky(headerOffset, adOffset, header, adSlot));

    document.querySelectorAll('button.show-sub-menu').forEach((el) => {
      el.addEventListener('click', (e) => {
        let target = e.target; // eslint-disable-line prefer-destructuring
        // If the span label instead of the button gets the click event,
        // reassign the target manually so the drawer can be located.
        if (e.target.nodeName === 'SPAN') {
          target = e.target.parentElement;
        }
        siblings(target, '.main-navigation__drawer').forEach((element) => {
          element.classList.add('sub-menu-is-visible');
        });
        document.querySelector('#site-navigation').classList.add('sub-menu-is-visible');
      });
    });

    document.querySelectorAll('.show-sub-sub-menu').forEach((el) => {
      el.addEventListener('click', () => {
        const { parentElement } = el;

        if (parentElement.classList.contains('sub-sub-menu-is-visible')) {
          parentElement.classList.remove('sub-sub-menu-is-visible');
        } else {
          parentElement.classList.add('sub-sub-menu-is-visible');
        }
      });
    });

    document.querySelectorAll('.hide-sub-menu').forEach((el) => {
      el.addEventListener('click', () => {
        document.querySelectorAll('.main-navigation__drawer').forEach((drawer) => {
          drawer.classList.remove('sub-menu-is-visible');
        });
        document.querySelector('#site-navigation').classList.remove('sub-menu-is-visible');
        document.querySelectorAll('.secondary-menu-item--has-children').forEach((element) => {
          element.classList.remove('sub-sub-menu-is-visible');
        });
      });
    });

    document.querySelector('.menu-toggle').addEventListener('click', () => {
      document.querySelector('.menu-toggle').classList.toggle('menu-is-visible');
      document.querySelector('.site-header__container').classList.toggle('menu-is-visible');
      document.querySelector('#site-navigation').classList.toggle('menu-is-visible');
    }, false);
  }
}
